// Domain enums for the schema.
//
// Day-precision dates are stored as "YYYY-MM-DD" strings rather than epoch
// milliseconds (see docs/person-affiliation-model.md §7): they sort and index
// correctly, and point-in-time reports do not drift with the timezone.
// Millisecond numbers are kept only where a real instant is meant.

export const AssignedRole = {
  STUDENT: "student",
  FACULTY: "faculty",
  ACTIVITY_ADMIN: "activityAdmin",
  GRANTS_ADMIN: "grantsAdmin",
  HR_ADMIN: "hrAdmin",
  SYS_ADMIN: "sysAdmin",
} as const;

export type AssignedRoleValue = (typeof AssignedRole)[keyof typeof AssignedRole];

// --- Person ----------------------------------------------------------------

// A person's status at a given date, derived from their affiliations (§5).
// Where several affiliations cover the date, the strongest status wins:
// counting beats non-counting.
export const PersonStatus = {
  CEMMIE: "cemmie",
  ALUMNI_ACTIVE: "alumniActive",
  ALUMNI: "alumni",
  INCOMING: "incoming",
  EXTERNAL: "external",
} as const;

export type PersonStatusValue = (typeof PersonStatus)[keyof typeof PersonStatus];

export const PersonNameKind = {
  // A name the person no longer uses. The display name always follows the
  // current one; research output stays findable under the previous form (§12).
  PREVIOUS: "previous",
  // A concurrent spelling or form the person also appears under.
  ALTERNATIVE: "alternative",
} as const;

export type PersonNameKindValue = (typeof PersonNameKind)[keyof typeof PersonNameKind];

export const NameSource = {
  DIRECTORY: "directory",
  HR: "hr",
  CURATION: "curation",
} as const;

export type NameSourceValue = (typeof NameSource)[keyof typeof NameSource];

// --- Account ---------------------------------------------------------------

// Graph userType. At least one person on the people directory holds a Guest
// account rather than a full one, which is neither "no account" nor a normal
// account and has to be visible as its own case.
export const AccountUserType = {
  MEMBER: "member",
  GUEST: "guest",
} as const;

export type AccountUserTypeValue = (typeof AccountUserType)[keyof typeof AccountUserType];

// Why an account exists without its holder being a CeMMie. Roughly 75 accounts
// are in this position and every one of them is legitimate, so the
// reconciliation report must be able to explain them rather than listing them
// as mysteries. Set by the sync from the documented exclusion groups; null
// means "no known reason", which is the case worth a human's attention.
export const AccountExclusionReason = {
  // Cluster-infrastructure agreement with the CCRI.
  CCRI_CLUSTER: "ccriCluster",
  // Sister institute.
  AITHYRA: "aithyra",
  // Medical University collaborators.
  EXTERNAL_COLLABORATOR: "externalCollaborator",
  // Technical consultants; no group membership at all.
  TECH_CONSULTANT: "techConsultant",
  // Held so a whole lab can use Microsoft Teams.
  TEAMS_ONLY: "teamsOnly",
  // Inside IT's normal 1-2 week window between a contract ending and the
  // account being disabled. Not an error, and not worth reporting.
  PENDING_DEACTIVATION: "pendingDeactivation",
} as const;

export type AccountExclusionReasonValue =
  (typeof AccountExclusionReason)[keyof typeof AccountExclusionReason];

// --- Organisational unit ---------------------------------------------------

export const OrgUnitType = {
  CORE: "core",
  ADJUNCT: "adjunct",
  FACILITY: "facility",
  ADMIN: "admin",
} as const;

export type OrgUnitTypeValue = (typeof OrgUnitType)[keyof typeof OrgUnitType];

// Entra groups that are not organisational units. Synced for convenience,
// never authoritative, and nothing is affiliated to them (§4).
export const ClassificationUnitKind = {
  FACULTY: "faculty",
  PHD_COHORT: "phdCohort",
  // Elected representative bodies. These Entra groups double as mailing lists,
  // so they contain administration staff who are not representatives -- which is
  // precisely why classification membership is never authoritative on its own.
  REPRESENTATIVE: "representative",
} as const;

export type ClassificationUnitKindValue =
  (typeof ClassificationUnitKind)[keyof typeof ClassificationUnitKind];

// --- Group membership ------------------------------------------------------

// Why a person holds an Entra group membership, materialised onto the row.
// UNEXPLAINED is allowed and is NOT an error: transitional access after an
// internal move looks exactly like this, and flagging it is what would make the
// reconciliation report unreadable (§4).
export const MembershipExplanation = {
  AFFILIATION: "affiliation",
  ROTATION: "rotation",
  UNEXPLAINED: "unexplained",
} as const;

export type MembershipExplanationValue =
  (typeof MembershipExplanation)[keyof typeof MembershipExplanation];

// --- Affiliation -----------------------------------------------------------

// The nature of the arrangement (§5). Deliberately finer than the legal
// instrument, because one instrument (the collaboration agreement) covers three
// quite different relationships and one relationship (hosted) has none at all.
export const AffiliationBasis = {
  // 1. Staff, interns, master's students, service staff, adjunct PIs. Owned by HR.
  EMPLOYMENT: "employment",
  // 2. Externally employed, hosted in a CeMM group. Owned by HR, PI as source.
  HOSTED_EXTERNAL: "hostedExternal",
  // 3. Collaboration agreement, working member. Owned by TTO.
  COLLABORATION_WORKING: "collaborationWorking",
  // 4. Collaboration agreement, short-term visitor. Owned by TTO.
  COLLABORATION_VISITOR: "collaborationVisitor",
  // 5. Collaboration agreement, wrap-up after leaving. Owned by TTO.
  COLLABORATION_WRAP_UP: "collaborationWrapUp",
} as const;

export type AffiliationBasisValue = (typeof AffiliationBasis)[keyof typeof AffiliationBasis];

// Which bases count towards the CeMMie headcount (§5). Derived from the basis
// rather than stored on the row, so it cannot drift from this table.
export const COUNTING_AFFILIATION_BASES: readonly AffiliationBasisValue[] = [
  AffiliationBasis.EMPLOYMENT,
  AffiliationBasis.HOSTED_EXTERNAL,
  AffiliationBasis.COLLABORATION_WORKING,
] as const;

export function basisCountsAsCeMMie(basis: AffiliationBasisValue): boolean {
  return COUNTING_AFFILIATION_BASES.includes(basis);
}

export const AffiliationSource = {
  // The CeMM people directory: the practical seed for the roster.
  DIRECTORY: "directory",
  // HR's monthly come-and-go list.
  COME_AND_GO: "comeAndGo",
  HR: "hr",
  TTO: "tto",
  PROGRAMME: "programme",
  // Entered or corrected by hand, including the known directory omissions.
  CURATION: "curation",
} as const;

export type AffiliationSourceValue = (typeof AffiliationSource)[keyof typeof AffiliationSource];

// Headship is carried by the affiliation rather than by the job title, because
// one person heads two facilities, one unit has two heads, and a PI holds the
// title "Principal Investigator" on a second affiliation they do not head
// (§13, and docs/person-affiliation-implementation.md §5).
export const HeadRole = {
  HEAD: "head",
  DEPUTY_HEAD: "deputyHead",
} as const;

export type HeadRoleValue = (typeof HeadRole)[keyof typeof HeadRole];

// --- Positions -------------------------------------------------------------

// European Framework for Research Careers. An optional crosswalk (§10): the
// internal vocabulary does not adopt the scheme.
export const CareerStage = {
  R1: "R1",
  R2: "R2",
  R3: "R3",
  R4: "R4",
} as const;

export type CareerStageValue = (typeof CareerStage)[keyof typeof CareerStage];

// Frascati Manual categories, for R&D statistics reporting. Also a crosswalk.
export const FrascatiCategory = {
  RESEARCHER: "researcher",
  TECHNICIAN: "technician",
  OTHER_SUPPORT: "otherSupport",
} as const;

export type FrascatiCategoryValue = (typeof FrascatiCategory)[keyof typeof FrascatiCategory];

// --- Programme enrollment --------------------------------------------------

export const Programme = {
  PHD: "phd",
  POSTDOC: "postdoc",
} as const;

export type ProgrammeValue = (typeof Programme)[keyof typeof Programme];

// How a programme is named on screen and in the audit log -- one list, so the
// editor, the programme pages and the log cannot drift apart.
export const PROGRAMME_LABELS: Record<ProgrammeValue, string> = {
  [Programme.PHD]: "PhD",
  [Programme.POSTDOC]: "Postdoc",
};

export const EnrollmentStatus = {
  ACTIVE: "active",
  INTERRUPTED: "interrupted",
  DISCONTINUED: "discontinued",
  COMPLETED: "completed",
} as const;

export type EnrollmentStatusValue = (typeof EnrollmentStatus)[keyof typeof EnrollmentStatus];

// --- Delegation ------------------------------------------------------------

export const DelegationScope = {
  ACTIVITIES: "activities",
} as const;

export type DelegationScopeValue = (typeof DelegationScope)[keyof typeof DelegationScope];

// Auto-revocation is recorded rather than silent, so the reason survives (§4:
// a delegation is bounded by both parties' affiliations).
export const DelegationRevocationReason = {
  MANUAL: "manual",
  HEAD_AFFILIATION_ENDED: "headAffiliationEnded",
  DELEGATE_AFFILIATION_ENDED: "delegateAffiliationEnded",
} as const;

export type DelegationRevocationReasonValue =
  (typeof DelegationRevocationReason)[keyof typeof DelegationRevocationReason];

// --- Activities ------------------------------------------------------------

// A reviewer is named one of three ways (model §9). The supervisor is NOT a
// role: it is resolved per person through their primary affiliation, so it
// cannot sit in a list of roles the way the admin roles can.
export const ReviewerKind = {
  ROLE: "role",
  SUPERVISOR: "supervisor",
  // The student's current co-supervisor (convex/coSupervision.ts), looked up
  // when it matters, like the supervisor: a change of co-supervisor moves the
  // open steps to the new one.
  CO_SUPERVISOR: "coSupervisor",
  PERSON: "person",
} as const;

export type ReviewerKindValue = (typeof ReviewerKind)[keyof typeof ReviewerKind];

export const ActivityReviewRole = {
  HR_ADMIN: "hrAdmin",
  GRANTS_ADMIN: "grantsAdmin",
  SYS_ADMIN: "sysAdmin",
  ACTIVITY_ADMIN: "activityAdmin",
} as const;

export type ActivityReviewRoleValue = (typeof ActivityReviewRole)[keyof typeof ActivityReviewRole];

// In what capacity a review was signed. The supervisor is the head of the
// person's primary group, and may be acted for by the deputy or a delegate;
// the record captures both who signed and in what capacity (§4, §14).
export const ReviewCapacity = {
  HEAD: "head",
  DEPUTY_HEAD: "deputyHead",
  DELEGATE: "delegate",
  ADMIN: "admin",
} as const;

export type ReviewCapacityValue = (typeof ReviewCapacity)[keyof typeof ReviewCapacity];

export const ActivityReviewMode = {
  ANY: "any",
  ALL: "all",
} as const;

export type ActivityReviewModeValue = (typeof ActivityReviewMode)[keyof typeof ActivityReviewMode];

export const PersonActivityStatus = {
  // Waiting: either an activity it depends on is unfinished, or its start day
  // has not arrived. Blocked is a state, not a hiding place -- the row stays
  // visible so people can plan for it.
  // The person is away. Distinct from blocked, which means waiting on a
  // prerequisite: nothing is expected of anybody while paused, no deadline
  // runs, and nothing is overdue (model §22).
  PAUSED: "paused",
  BLOCKED: "blocked",
  NOT_STARTED: "not_started",
  IN_PROGRESS: "in_progress",
  AWAITING_REVIEW: "awaiting_review",
  NEEDS_REVISION: "needs_revision",
  COMPLETED: "completed",
  CANCELLED: "cancelled",
  // On departure or a discontinued enrollment, open work is archived -- not
  // auto-completed and not deleted. Completed work is retained
  // (docs/events-activities-model.md §10).
  ARCHIVED: "archived",
} as const;

export type PersonActivityStatusValue =
  (typeof PersonActivityStatus)[keyof typeof PersonActivityStatus];

// How someone came to hold an activity. With `required`, this is the whole of
// the expectation (model §7): assigned + required is required, assigned + not
// required is *encouraged*, and self-enrolled is voluntary.
//
// SELF_ENROLLED is unreachable until the offer link exists (model §6): today
// every person activity is assigned. The value is here because the distinction
// is the design, and because a writer that already knows the answer costs
// nothing to record.
export const PersonActivityOrigin = {
  ASSIGNED: "assigned",
  SELF_ENROLLED: "selfEnrolled",
} as const;

export type PersonActivityOriginValue =
  (typeof PersonActivityOrigin)[keyof typeof PersonActivityOrigin];

// What a date is stated relative to (model §8). Materialised as a day, and the
// stored day is what counts from then on -- recomputed only when the thing it
// is anchored to actually moves (model §3.4). Most resolve at assignment and
// never move again; the two that do neither say so on themselves.
export const DateAnchor = {
  // "By the end of year 3." Only resolvable where a programme enrollment
  // exists, which is why resolution failure has to be visible rather than
  // silently treated as "today".
  ENROLLMENT_START: "enrollmentStart",
  // "Within four weeks of starting" -- onboarding, for employees and guests.
  AFFILIATION_START: "affiliationStart",
  // An institute deadline that falls on a day regardless of who is involved.
  FIXED_DAY: "fixedDay",
  // The fallback, and what the module did before anchors existed.
  ASSIGNMENT_DAY: "assignmentDay",
  // "Two weeks after the workshop." Reads the event the person's copy is
  // anchored to, which is what makes everything attached to an occasion move
  // together when the occasion moves (model §6, link 1).
  EVENT_DATE: "eventDate",
  // "Three months after the faculty approved it." Waits on the activities
  // this one already declares as its predecessors, so it names no second
  // relationship of its own: the day is the day they were all satisfied.
  // Unresolvable until then, which is why it is the one anchor materialised
  // after assignment rather than at it (model §25).
  AFTER_ACTIVITY: "afterActivity",
} as const;

export type DateAnchorValue = (typeof DateAnchor)[keyof typeof DateAnchor];

export const ActivityTaskStatus = {
  NOT_STARTED: "not_started",
  DRAFT: "draft",
  AWAITING_REVIEW: "awaiting_review",
  NEEDS_REVISION: "needs_revision",
  APPROVED: "approved",
} as const;

export type ActivityTaskStatusValue = (typeof ActivityTaskStatus)[keyof typeof ActivityTaskStatus];

// ActivityDependencyKind is gone. It had two members: "activity", which is now
// the only thing a dependency expresses, and "date", which was never a
// dependency at all -- "not before N days" is a start rule, and lives on the
// activity as `startRule` (model §8).

export const ActivityReviewDecision = {
  APPROVED: "approved",
  REVISION_REQUESTED: "revision_requested",
} as const;

export type ActivityReviewDecisionValue =
  (typeof ActivityReviewDecision)[keyof typeof ActivityReviewDecision];

// Who performs a task, as opposed to who signs it off. The three kinds after
// ASSIGNEE mirror ReviewerKind exactly, so the same resolution and the same
// picker serve both (model §23, plan §20.2 item 1).
// Where a task's own deadline is measured from. Deliberately only two, and
// both relative to something the task already knows: a task is positioned
// against its activity or against its own opening, never against a fresh
// anchor of its own (model §22).
export const TaskDateBasis = {
  // "Two weeks before the meeting" -- the activity's due day, minus the offset.
  BEFORE_ACTIVITY_DUE: "beforeActivityDue",
  // "Respond within five days" -- counted from the moment the task unlocks,
  // which is what makes a self-reported date usable as a trigger.
  AFTER_TASK_OPENS: "afterTaskOpens",
} as const;

export type TaskDateBasisValue = (typeof TaskDateBasis)[keyof typeof TaskDateBasis];

export const ActorKind = {
  ASSIGNEE: "assignee",
  ROLE: "role",
  SUPERVISOR: "supervisor",
  CO_SUPERVISOR: "coSupervisor",
  PERSON: "person",
} as const;

export type ActorKindValue = (typeof ActorKind)[keyof typeof ActorKind];

// A catalogue step whose actor or reviewer is chosen when the activity is
// assigned: "Named when assigned". It exists ONLY in the catalogue. Assigning
// replaces it with the person chosen, so an assigned copy never holds it and
// everything downstream -- queues, notifications, permissions -- sees an
// ordinary named person.
export const NAMED_AT_ASSIGNMENT = "namedAtAssignment" as const;

export const ActivityFormFieldType = {
  TEXT: "text",
  TEXTAREA: "textarea",
  DATE: "date",
  SELECT: "select",
  URL: "url",
  BOOLEAN: "boolean",
  FILE: "file",
  NUMBER: "number",
  EMAIL: "email",
  RANGE: "range",
  TIME: "time",
} as const;

export type ActivityFormFieldTypeValue =
  (typeof ActivityFormFieldType)[keyof typeof ActivityFormFieldType];


// --- Events and participation ----------------------------------------------

// What somebody did at an occasion. Talks and posters are separate roles
// rather than one "presenter": they are different contributions on a CV. The
// distinction is a conference one -- the Friday Seminar has speakers and
// hosts only (model §5).
export const ParticipationRole = {
  ATTENDEE: "attendee",
  // A contributed talk, and also the Friday Seminar slot. Kept distinct from
  // INVITED_LECTURER because being asked to come and speak is not the same
  // achievement as having an abstract accepted, and the two read differently
  // on a CV -- the same argument that keeps a poster separate from a talk.
  SPEAKER: "speaker",
  INVITED_LECTURER: "invitedLecturer",
  POSTER_PRESENTER: "posterPresenter",
  // The Friday Seminar's chair. Not a conference role.
  HOST: "host",
  ORGANISER: "organiser",
} as const;

export type ParticipationRoleValue = (typeof ParticipationRole)[keyof typeof ParticipationRole];

// Only CONFIRMED satisfies a task. PLANNED is a scheduled slot or an accepted
// sign-up -- a statement about the future, which is what makes the empty-slot
// view and the reminder possible; the roster turns it into a fact afterwards
// (model §11).
export const ParticipationState = {
  PLANNED: "planned",
  WAITLISTED: "waitlisted",
  CONFIRMED: "confirmed",
  NO_SHOW: "noShow",
  CANCELLED: "cancelled",
} as const;

export type ParticipationStateValue = (typeof ParticipationState)[keyof typeof ParticipationState];

export const EventKind = {
  SEMINAR: "seminar",
  WORKSHOP: "workshop",
  INSTITUTE_DAY: "instituteDay",
  OTHER: "other",
} as const;

export type EventKindValue = (typeof EventKind)[keyof typeof EventKind];

// An occurrence is cancelled, never deleted: people signed up for it, and a
// row that vanishes takes their record of having been there with it.
export const EventStatus = {
  SCHEDULED: "scheduled",
  CANCELLED: "cancelled",
} as const;

export type EventStatusValue = (typeof EventStatus)[keyof typeof EventStatus];

// Conferences and courses are participations with NO event row: the occasion's
// details ride inline on the participation instead. Deliberate, and costly in
// a known way -- five people at one conference make five unjoinable records --
// bought in exchange for a curated calendar that stays small (model §13).
export const ExternalOccasionKind = {
  CONFERENCE: "conference",
  COURSE: "course",
  OTHER: "other",
} as const;

export type ExternalOccasionKindValue =
  (typeof ExternalOccasionKind)[keyof typeof ExternalOccasionKind];

// --- Notifications ---------------------------------------------------------

export const NotificationCategory = {
  ACTION_REQUIRED: "action_required",
  UPDATE: "update",
  SYSTEM: "system",
  SECURITY: "security",
  INVITATION: "invitation",
} as const;

export type NotificationCategoryValue =
  (typeof NotificationCategory)[keyof typeof NotificationCategory];

// --- Audit log -------------------------------------------------------------

// How an entry came about. "system" covers both a scheduled job and an
// operator running an internal function from the CLI: neither has a signed-in
// identity, and Convex gives a mutation no way to tell the two apart, so
// claiming one or the other would sometimes be a guess.
export const AuditVia = {
  APP: "app",
  SYSTEM: "system",
} as const;

export type AuditViaValue = (typeof AuditVia)[keyof typeof AuditVia];

// What an entry is about. A module joins the log by adding its own types here
// -- research outputs and grants will -- which only ever widens the validator,
// so it is always a safe deploy.
export const AuditEntityType = {
  ACTIVITY: "activity",
  ACTIVITY_TAG: "activityTag",
  PERSON_ACTIVITY: "personActivity",
  EVENT_SERIES: "eventSeries",
  EVENT: "event",
  PARTICIPATION: "participation",
  PERSON: "person",
  ACCOUNT: "account",
  AFFILIATION: "affiliation",
  PROGRAMME_ENROLLMENT: "programmeEnrollment",
} as const;

export type AuditEntityTypeValue = (typeof AuditEntityType)[keyof typeof AuditEntityType];

// Acts, not writes (cicd-backlog §P14): what a person or the system DID, named
// `<module>.<verb>`. A module adds its own here, like entity types above.
export const AuditAction = {
  // The catalogue. Editing an entry never reaches work already under way, so
  // these say nothing about anybody in particular.
  ACTIVITY_CREATED: "activity.created",
  ACTIVITY_UPDATED: "activity.updated",
  ACTIVITY_CLONED: "activity.cloned",
  ACTIVITY_ACTIVATED: "activity.activated",
  ACTIVITY_DEACTIVATED: "activity.deactivated",
  ACTIVITY_DELETED: "activity.deleted",
  ACTIVITY_TAG_CREATED: "activityTag.created",
  ACTIVITY_TAG_UPDATED: "activityTag.updated",
  ACTIVITY_TAG_DELETED: "activityTag.deleted",

  PERSON_ACTIVITY_ASSIGNED: "personActivity.assigned",
  // The copy is gone; its entries stay. That a piece of somebody's work was
  // deleted, and by whom, is exactly what an audit log is for.
  PERSON_ACTIVITY_DELETED: "personActivity.deleted",
  PERSON_ACTIVITY_STATUS_SET: "personActivity.statusSet",
  PERSON_ACTIVITY_EDITED: "personActivity.edited",
  PERSON_ACTIVITY_REASSIGNED: "personActivity.reassigned",
  PERSON_ACTIVITY_ARCHIVED: "personActivity.archived",
  // Cancelled with the event it was made for, never on its own.
  PERSON_ACTIVITY_CANCELLED: "personActivity.cancelled",
  PERSON_ACTIVITY_DATES_MOVED: "personActivity.datesMoved",
  PERSON_ACTIVITY_PAUSED: "personActivity.paused",
  PERSON_ACTIVITY_RESUMED: "personActivity.resumed",
  // Recorded ONLY when somebody else saved it, i.e. while impersonating. A
  // person's own draft affects nobody, and logging it would record how people
  // work rather than what they did.
  PERSON_ACTIVITY_TASK_DRAFT_SAVED: "personActivity.taskDraftSaved",
  PERSON_ACTIVITY_TASK_SUBMITTED: "personActivity.taskSubmitted",
  // Filled in by somebody other than the assignee: a supervisor's assessment,
  // a chair confirming attendance.
  PERSON_ACTIVITY_TASK_COMPLETED_BY_ACTOR: "personActivity.taskCompletedByActor",
  PERSON_ACTIVITY_TASK_APPROVED: "personActivity.taskApproved",
  PERSON_ACTIVITY_TASK_REVISION_REQUESTED: "personActivity.taskRevisionRequested",
  // A step completed by a participation record rather than by a form: the
  // roster confirmed, or the person cited a conference they went to.
  PERSON_ACTIVITY_TASK_COMPLETED_BY_RECORD: "personActivity.taskCompletedByRecord",
  // ...and opened again because that record stopped being a fact.
  PERSON_ACTIVITY_TASK_REOPENED: "personActivity.taskReopened",

  EVENT_SERIES_CREATED: "eventSeries.created",
  EVENT_SERIES_UPDATED: "eventSeries.updated",
  EVENT_SERIES_ACTIVATED: "eventSeries.activated",
  EVENT_SERIES_DEACTIVATED: "eventSeries.deactivated",
  EVENT_SERIES_OCCURRENCES_GENERATED: "eventSeries.occurrencesGenerated",
  EVENT_CREATED: "event.created",
  EVENT_UPDATED: "event.updated",
  EVENT_MOVED: "event.moved",
  EVENT_CANCELLED: "event.cancelled",
  // Attached, replaced, taken down, or its link rotated. Never a response:
  // those are anonymous by construction.
  EVENT_FEEDBACK_FORM_SET: "event.feedbackFormSet",
  EVENT_ROSTER_CONFIRMED: "event.rosterConfirmed",

  PARTICIPATION_SIGNED_UP: "participation.signedUp",
  PARTICIPATION_WITHDRAWN: "participation.withdrawn",
  PARTICIPATION_STATE_SET: "participation.stateSet",
  PARTICIPATION_SCHEDULED: "participation.scheduled",
  PARTICIPATION_RECORDED: "participation.recorded",
  PARTICIPATION_UPDATED: "participation.updated",
  PARTICIPATION_DELETED: "participation.deleted",

  // The directory. A sync or import run writes ONE summary entry with its
  // counts, plus an entry for each consequential change -- never one per
  // account looked at, which would bury the row somebody needed under 392
  // nights of nothing.
  DIRECTORY_SYNC_RUN: "directory.syncRun",
  DIRECTORY_IMPORT_RUN: "directory.importRun",
  // A person the sync saw in Entra for the first time.
  DIRECTORY_PERSON_CREATED: "directory.personCreated",
  DIRECTORY_ACCOUNT_REMOVED: "directory.accountRemoved",
  DIRECTORY_ACCOUNT_RESTORED: "directory.accountRestored",
  DIRECTORY_COHORT_FILLED: "directory.cohortFilled",
  // Curation: a human asserting an organisational fact.
  AFFILIATION_ADDED: "affiliation.added",
  // An open affiliation's headship or position corrected in place.
  AFFILIATION_CORRECTED: "affiliation.corrected",
  AFFILIATION_ENDED: "affiliation.ended",
  AFFILIATION_PRIMARY_SET: "affiliation.primarySet",
  // Taken back by whoever submitted it, before anybody signed. Deliberately
  // NOT a revision request: that is a reviewer's judgement on the work, and
  // recording somebody's own typo as one would read, for ever, as if their
  // submission had been found wanting.
  PERSON_ACTIVITY_TASK_WITHDRAWN: "personActivity.taskWithdrawn",
  // Recorded by the supervisor or an activity admin. Setting one while another
  // is current ends that one: "set" names the new co-supervisor, "ended" is
  // only for ending without a replacement.
  // Who does a step of one copy, or who signs it off, changed by an admin.
  PERSON_ACTIVITY_TASK_PEOPLE_CHANGED: "personActivity.taskPeopleChanged",
  // Named and ended by the group head; also ended by the nightly refresh when
  // either person leaves the group, which is recorded as the system's act.
  // An admin role given or taken away on Administration > Roles, or seeded
  // from the configuration by the first sync of a deployment.
  ROLE_GRANTED: "role.granted",
  ROLE_REVOKED: "role.revoked",
  DELEGATION_GRANTED: "delegation.granted",
  DELEGATION_REVOKED: "delegation.revoked",
  CO_SUPERVISOR_SET: "coSupervision.set",
  CO_SUPERVISION_ENDED: "coSupervision.ended",
} as const;

export type AuditActionValue = (typeof AuditAction)[keyof typeof AuditAction];
